import { Box, HStack, Text } from "@chakra-ui/react";
import { useState } from "react";

import { DocumentDto, CloudSubscriptionDto } from "@bcasmart/doxecure-commons/doxecureClientTypes";
import { $count, $ok } from "foundation-ts/commons";
import { $bytes } from "foundation-ts/number";
import { UUID } from "foundation-ts/types";

import { DocumentCard } from "@components/Cards/DocumentCard";
import { DataCountDisplay } from "@/components/DataCountDisplay";
import { SeaShellBox } from "@components/SeaShellBox";
import { SectionBox } from "@components/SectionBox/SectionBox";
import { SectionBoxFooter } from "@components/SectionBox/SectionBoxFooter";
import { AddDocumentModal } from "@containers/signature/AddDocumentModal";
import { config } from "@/config/config";
import { useStyles } from "@hooks/useStyles";
import { localtrans } from "@utils/LocalTranslations";

interface DocumentsToSignBoxProps {
    documents: DocumentDto[];
    drives: CloudSubscriptionDto[];
    onSelectDrive: (drive: CloudSubscriptionDto) => void;
    onClickUploadFiles: () => void;
    onViewDocument: (apid: UUID) => void;
}

export const DocumentsToSignBox = (props: DocumentsToSignBoxProps) => {
    const { documents, drives, onSelectDrive, onClickUploadFiles, onViewDocument } = props;
    const { sectionStyle, itemStyle } = useStyles();
    const [openAddDocumentModal, setOpenAddDocumentModal] = useState<boolean>(false) ;
    const [forceReloadFooter, setForceReloadFooter] = useState<number>(0) ;

    const handleOpenAddDocumentModal = () => {
        setOpenAddDocumentModal(true) ;
    } ;

    const handleCloseAddDocumentModal = () => {
        setForceReloadFooter(forceReloadFooter + 1) ;
        setOpenAddDocumentModal(false) ;
    } ;

    const handleSelectDrive = (drive: CloudSubscriptionDto) => {
        setForceReloadFooter(forceReloadFooter + 1) ;
        setOpenAddDocumentModal(false) ;
        onSelectDrive(drive) ;
    } ;

    const handleClickUploadFiles = () => {
        setForceReloadFooter(forceReloadFooter + 1) ;
        setOpenAddDocumentModal(false) ;
        onClickUploadFiles() ;
    } ;

    const renderDocumentCard = (item: DocumentDto) => {
        // FIXME: should have something else to manage that here
        if (!$ok(item.apid) && !$ok(item.localUUID)) { throw '<DocumentsToSignBox>.renderDocumentCard(): document.apid and document.localUUID should not be both undefined'; }
        return (
            <DocumentCard
                key={item.apid || item.localUUID!}
                item={item}
                itemStyle={itemStyle.document}
                displayIcon
                onClick={() => onViewDocument(item.apid || item.localUUID!)}
            />
        ) ;
    } ;

   const renderAddDocumentButton = () => {
        return <SectionBoxFooter key={forceReloadFooter} component="documents" title="Ajouter un document" disabled={$count(documents) === config.session.maxNbDoc || _sessionSize(documents) > config.session.maxSessionSize} onClick={handleOpenAddDocumentModal} /> ;
    }

    const SessionSizeDisplay = () => (
        <Box fontSize="small" borderRadius={5} borderWidth={1} borderStyle="solid" borderColor={_sessionSize(documents) > config.session.maxSessionSize ? "red" : "darkgray"} padding="0 5px" color={_sessionSize(documents) > config.session.maxSessionSize ? "red" : "darkgray"}>
            {_sessionSize(documents) === 0 ? 0 : $bytes(_sessionSize(documents))} / {config.session.maxSessionSize/(1024*1024)}Mo
        </Box>
    );

    return (
        <>
            <SeaShellBox component="form">
                <HStack>
                    <Text fontWeight="bold">{localtrans('SignatureDocumentsSection')}<sup style={{color:"red"}}> *</sup></Text>
                    <DataCountDisplay count={$count(documents)} maxDataCount={config.session.maxNbDoc} />
                    <SessionSizeDisplay />
                </HStack>
                <Box mt="0">
                    <Text fontSize="small">{`Un dossier de signature peut contenir au maximum ${config.session.maxNbDoc} documents, chaque document ayant une taille maximale de ${config.session.maxDocSize/(1024*1024)}Mo.`}</Text>
                    <Text fontSize="small">{`Un dossier de signature ne peut pas dépasser ${config.session.maxSessionSize/(1024*1024)}Mo.`}</Text>
                </Box>
                <SectionBox
                    items={documents}
                    sectionStyle={sectionStyle.documents}
                    renderItem={renderDocumentCard}
                    renderFooter={renderAddDocumentButton}
                />
            </SeaShellBox>
            <AddDocumentModal
                drives={drives}
                isOpen={openAddDocumentModal}
                onClose={handleCloseAddDocumentModal}
                onSelectDrive={handleSelectDrive}
                onClickUploadFiles={handleClickUploadFiles}
            />
        </>
    ) ;
} ;

function _sessionSize(documents: DocumentDto[]) {
    return documents.reduce((total, doc) => total += doc.size, 0);
}